import { io, type Socket } from 'socket.io-client';
import { ensureAccessToken, getAccessToken } from './api';
import { useAuthStore } from '../store/auth';
import { useUiStore } from '../store/ui';

let socket: Socket | null = null;
let connectErrorLogged = false;

/**
 * Built-in handler for `tenant.suspended`, registered on the socket itself so
 * it survives every connect/disconnect cycle (the AuthInitializer's handler is
 * lost once a page-level `connectSocket()` re-creates the socket after login).
 * Clears the local session → ProtectedRoute bounces to /login, with the
 * server-supplied reason in a toast.
 */
function handleServerEvent(event: unknown): void {
  const ev = event as { type?: string; data?: { reason?: string } } | null;
  if (ev?.type !== 'tenant.suspended') return;
  useAuthStore.getState().clear();
  disconnectSocket();
  useUiStore
    .getState()
    .toast('warning', 'Account suspended', ev.data?.reason ?? 'Your tenant account has been suspended.');
}

/** Authenticated realtime socket (engine path `/ws`, default namespace). */
export function connectSocket(onEvent?: (event: unknown) => void): Socket | null {
  // No token yet (logged out / session not restored) — skip connecting.
  // Callers re-invoke connectSocket() after login, which will connect then.
  if (!getAccessToken()) return socket;

  if (!socket) {
    socket = io({
      path: '/ws',
      transports: ['polling'],
      // Refresh a possibly-expired access token before every (re)connect.
      // Without this the gateway kills the handshake with the revoked token,
      // spamming "WebSocket connection ... closed before receiving a
      // handshake response" on every retry until the page is reloaded.
      auth: async (callback) => callback({ token: (await ensureAccessToken()) ?? '' }),
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });
    // Server drops unauthenticated handshakes by design; log once and keep
    // retrying quietly instead of spamming the console per attempt.
    socket.on('connect_error', () => {
      if (!connectErrorLogged) {
        connectErrorLogged = true;
        // eslint-disable-next-line no-console
        console.debug('[ws] connection refused (likely no/expired token) — retrying quietly');
      }
    });
    socket.on('connect', () => {
      connectErrorLogged = false;
    });
    socket.on('event', handleServerEvent);
  }
  if (onEvent && !socket.listeners('event').includes(onEvent)) {
    socket.on('event', onEvent);
  }
  return socket;
}

export function disconnectSocket(): void {
  if (!socket) return;
  if (socket.connected) {
    socket.disconnect();
  } else {
    // Handshake still in flight — closing the raw WebSocket now makes the
    // browser log "WebSocket is closed before the connection is established".
    // Let it connect, then close quietly.
    const s = socket;
    s.once('connect', () => s.disconnect());
  }
  socket = null;
}

/**
 * Register a realtime-event handler on the shared socket. Returns an
 * unsubscribe that only removes this page's listener — it never destroys the
 * app-wide socket (that would kill every page's realtime feed on navigation).
 */
export function subscribeRealtimeEvents(onEvent: (event: unknown) => void): () => void {
  connectSocket();
  socket?.on('event', onEvent);
  return () => {
    socket?.off('event', onEvent);
  };
}
